import {
  ApiFaultOrWarnings,
  MicroserviceFault
} from '../api/api.fault.interfaces';
import { Application } from '../application/application';
import { ApplicationContextProps } from '../application/application.context.interfaces';
import { TFunction } from '../hooks/useTranslation.hook';
import { HttpRequestConfig, HttpResponse } from '../interfaces';
import { DebugManager } from '../utility/DebugManager';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Error Types, Subtypes, Severity (from errorHandling.constants)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Types of errors that can be encountered during application runtime. */
export enum ErrorTypes {
  API = 1,
  APPLICATION = 2,
  UNKNOWN = 3
}

// prettier-ignore
export enum ErrorSubtypes {
  FAULT = 1,              // API Fault
  NETWORK = 2,            // Network- or Browser-level error
  RUNTIME = 3,            // Application runtime error
  SERVER = 4,             // Non-"200 OK" server response
  TIMEOUT = 5,            // Request/response timeout
  UNHANDLED_PROMISE = 6,  // Unhandled promise rejection
  UNKNOWN = 7
}

/**
 * Severity of an error.
 *
 * NOTE: Values correspond to API fault severity.
 */
export enum ErrorSeverity {
  /** Error that prevents system from functioning. */
  Fatal = 1,
  /** Error that prevents a request from processing. */
  Error = 2,
  /** Warning that does not result in an aborted request, but is not common processing behavior. */
  Warning = 3,
  /** Error that is expected under normal processing due to invalid requests, etc. */
  Functional = 4,
  /** Informational messages generated by the application. */
  Informational = 5,
  /** Debugging messages generated by the application. */
  Debug = 6
}

/**
 * Error messages that should be ignored during local development and
 * not submitted to the logging endpoint.
 */
export const ERRORS_IGNORED_IN_DEV = {
  LENGTH_OF_UNDEFINED: "Cannot read property 'length' of undefined",
  MODULE_BUILD_FAILED: 'Module build failed',
  NOT_A_FUNCTION: 'is not a function',
  NOT_DEFINED: 'is not defined',
  TOO_MANY_RERENDERS: 'Too many re-renders'
};

/** The name provided to error types. */
export const ERROR_TYPE_NAMES = {
  [ErrorSubtypes.RUNTIME]: 'Runtime Error',
  [ErrorSubtypes.UNHANDLED_PROMISE]: 'Unhandled Promise Rejection Error',
  [ErrorTypes.APPLICATION]: 'Application Error'
};

/** Mapping of error subtype to severity. */
export const ErrorSubtypesSeverity = {
  [ErrorSubtypes.FAULT]: ErrorSeverity.Error,
  [ErrorSubtypes.NETWORK]: ErrorSeverity.Fatal,
  [ErrorSubtypes.RUNTIME]: ErrorSeverity.Fatal,
  [ErrorSubtypes.SERVER]: ErrorSeverity.Error,
  [ErrorSubtypes.TIMEOUT]: ErrorSeverity.Error,
  [ErrorSubtypes.UNHANDLED_PROMISE]: ErrorSeverity.Functional,
  [ErrorSubtypes.UNKNOWN]: ErrorSeverity.Informational
};

export const DEFAULT_ERROR_SEVERITY = ErrorSeverity.Informational;
export const DEFAULT_ERROR_SUBTYPE = ErrorSubtypes.UNKNOWN;
export const DEFAULT_ERROR_TYPE = ErrorTypes.UNKNOWN;

export type ServerErrorHandlerType = 'onInternalServerError';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Log Message Types (from errorHandling.logging.interfaces)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export type ComponentNameType = 'AscendonUI.' | Application | '';

/** An individual log message. */
export interface LogItem {
  /** API fault code. */
  Code: null | number;

  /**
   * Application module (e.g. "AscendonUI.Workbench") where the event occurred;
   * maximum of 50 characters.
   */
  ComponentName: ComponentNameType;

  /** Message; maximum of 1,000 characters. */
  Message: string;

  /** URL _path_ where the event occurred; maximum of 100 characters. */
  OriginatingOperation: null | string;

  /**
   * Severity of the event; determines whether event is forwarded to reporting depending upon
   * environment type (e.g. Client Test).
   */
  Severity?: ErrorSeverity;

  /** Name of component or source file where the event occurred; maximum of 50 characters. */
  SubComponentName: string;
}

/**
 * Collection of log messages.
 *
 * NOTE: When logging an API-related event, the collection will contain up to 4 items with
 * only the `Message` property differing between them:
 *
 * - The first item will include the API message _configuration_
 * - The second item will include the API message _request_
 * - The third item will include the API message _response_
 * - The fourth item will include the "browsing", "error" and "user" contexts
 */
export type LogItems = LogItem[];

/** The request payload when submitting to the logging service. */
export interface LogMessageRequest {
  ContextId?: string;

  /** Collection of log messages; maximum of 10. */
  Messages: LogItems;
}

/** The response payload upon submission to the logging service. */
export interface LogMessageResponse {
  /**
   * Unique identifier for the transaction.
   *
   * NOTE: May be used to correlate multiple log submissions (e.g.
   * the logs from Submission A resulted in Submission B).
   */
  ContextId?: string;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Captured Error Types (from errorHandling.capturedError.interfaces)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Parameters when enhancing a captured error and submitting to logging service. */
export type EnhanceAndSubmitCapturedError = EnhanceCapturedErrorParams & {
  apiFunc: (payload: LogMessageRequest) => Promise<any>;
};

/** Parameters when enhancing a captured error. */
export interface EnhanceCapturedErrorParams {
  applicationContext: ApplicationContextProps;
  capturedError: CapturedError;
  userContext: {
    displayLanguage: string;
    language: string;
  };
}

/** The "API message" (e.g. request/response) portion of a captured error. */
export interface CapturedErrorApiMessage<T = any> {
  config: HttpRequestConfig;
  request?: any;
  response?: HttpResponse<T>;
}

/** Locations on a captured error where the "API message" configuration exists. */
export type CapturedErrorApiMessageConfigPath =
  | 'config'
  | 'request.config'
  | 'response.config';

export interface CapturedErrorBase {
  applicationContext: {
    apiMessage: {};
    moduleName: ComponentNameType;
    moduleVersion?: string;
  };
  browsingContext: {
    url: string;
    userAgent: string;
  };
  errorContext: {};

  /** Indicate this is a "captured error" and not a raw one. */
  isCaptured: boolean;
  isHandled: boolean;
  message: string;
  name: string;
  severity?: ErrorSeverity;
  subtype: ErrorSubtypes;
  type: ErrorTypes;
  userContext: {
    displayLanguage: string;
    language: string;
    translatedMessage?: string;
  };
}

/** Captured error that is API-related (e.g. HTTP error or API fault). */
export interface CapturedApiError<T = any> extends CapturedErrorBase {
  applicationContext: {
    apiMessage: CapturedErrorApiMessage<T>;
    moduleName: ComponentNameType;
    moduleVersion?: string;
  };
  errorContext: {
    /** The error "code" returned in the response (e.g. "ECONNABORTED"). */
    code?: string | null;
    message?: string | null;
    stack?: string;
  };
  isBodyOrPayloadRedacted: boolean;
  type: ErrorTypes.API;
  subtype:
    | ErrorSubtypes.FAULT
    | ErrorSubtypes.NETWORK
    | ErrorSubtypes.SERVER
    | ErrorSubtypes.TIMEOUT
    | ErrorSubtypes.UNKNOWN;
}

/** Captured error for an API fault. */
export interface CapturedApiFaultError
  extends Omit<CapturedApiError, 'errorContext'> {
  applicationContext: {
    apiMessage: CapturedErrorApiMessage<ApiFaultOrWarnings | MicroserviceFault>;
    moduleName: ComponentNameType;
    moduleVersion?: string;
  };
  errorContext: {
    faultCode?: number | null;
    faultSubcode?: number | null;
    message?: string | null;
  };
  type: ErrorTypes.API;
  subtype: ErrorSubtypes.FAULT;
}

/** Captured error for a runtime, application-level error. */
export interface CapturedRuntimeError extends CapturedErrorBase {
  errorContext: {
    columnNumber: number;
    filename: string;
    lineNumber: number;
    message: string;
    stack?: string;
  };
  type: ErrorTypes.APPLICATION;
  subtype: ErrorSubtypes.RUNTIME;
}

/** Captured error for an "unhandled promise rejection" (e.g. a missing `catch`). */
export interface CapturedUnhandledPromiseRejectionError
  extends CapturedErrorBase {
  errorContext: {
    message: string;
    stack: any;
  };
  type: ErrorTypes.APPLICATION;
  subtype: ErrorSubtypes.UNHANDLED_PROMISE;
}

/** Standardized version of API or runtime errors captured for submission to logging service. */
export type CapturedError<T = any> =
  | CapturedApiError<T>
  | CapturedApiFaultError
  | CapturedRuntimeError
  | CapturedUnhandledPromiseRejectionError;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ErrorHandler Configuration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface ErrorHandlerConfig {
  debugManager?: DebugManager;
  showToast?: (message: string) => void;
  triggerServerErrorEvent?: (error: CapturedApiError) => void;
  submitLog?: (payload: LogMessageRequest) => Promise<unknown>;
  applicationContext?: ApplicationContextProps;
  userContext?: ErrorHandlerUserContext;
  deduplicationTTL?: number;
  /** Translation function (e.g. i18next.t). Used by the fault message translation pipeline. */
  translateFn?: TFunction;
  /** Check whether a translation key exists (e.g. i18next.exists). */
  translationExistsFn?: (key: string) => boolean;
}

export interface ErrorHandlerUserContext {
  displayLanguage: string;
  language: string;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Error Handling Options
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface ErrorHandlingOptions {
  errorHandling?: ErrorHandlingMode;
  /**
   * Custom error message translator. Called first in the translation priority
   * chain. Return a string to use as the toast message, or undefined/null to
   * fall through to the standard translation chain.
   */
  translateMessage?: (error: CapturedApiError) => string | undefined | null;
  /**
   * Fault codes that should be handled by `customFaultHandler` instead of
   * the default toast notification.
   */
  customHandledFaultCodes?: { code: number; subCode?: number }[];
  customFaultHandler?: (params: { capturedError?: CapturedApiError }) => void;
  /** Scope identifier for scoped configuration lookup. */
  scope?: string;
}

export type ErrorHandlingMode = 'toast' | 'errorBoundary' | 'unhandled';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Error Handling Result
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface ErrorHandlingResult {
  mode: ErrorHandlingMode;
  translatedMessage: string;
  shouldTriggerBoundary: boolean;
  capturedError: CapturedApiError;
  deduplicated: boolean;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Runtime Error Context
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface RuntimeErrorContext {
  domain?: string;
  tier?: string;
  instanceLabel?: string;
  componentStack?: string | null;
}

export interface RuntimeErrorMetadata {
  domain?: string;
  tier?: string;
  instanceLabel?: string;
  componentStack?: string | null;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Middleware
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export type ErrorMiddlewareNext = (error: CapturedError) => void;

export type ErrorMiddleware = (
  error: CapturedError,
  next: ErrorMiddlewareNext
) => void;
